/* -----------------------------------------------------------------------------
 *
 * CUSTOM COMPONENT - Uses react-select
 *
 * This is a custom multi-select component that uses react-select/creatable
 * to provide multi-select and creatable functionality not available in
 * shadcn's standard select component.
 *
 * Related: See FIXME comment in src/components/Select.tsx
 *
 * Style: Uses Tailwind CSS utility classes and cn() for proper integration
 * with the solink-ui design system. Colors reference CSS variables from
 * tailwind.config.ts for theme consistency.
 *
 * ----------------------------------------------------------------------------- */

import React, { useMemo, useState, type WheelEvent } from 'react';
import {
  components,
  type ControlProps,
  type GroupBase,
  type InputProps,
  type MenuProps,
  type MultiValueGenericProps,
  type MultiValueRemoveProps,
  type OptionProps,
  type SelectComponentsConfig,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';

import type { PortaledSurfaceVariant } from '../lib/portaledSurface';
import { cn } from '../lib/utils';

export interface Option {
  readonly label: string;
  readonly value: string;
}

export interface OptionGroup {
  readonly label: string;
  readonly options: readonly Option[];
}

interface MultiSelectProps {
  /** Accepts a flat list of options or grouped options (renders a separator per group). */
  options?: ReadonlyArray<Option | OptionGroup>;
  value?: Option[];
  onChange?: (value: Option[]) => void;
  onCreateOption?: (inputValue: string) => Promise<Option> | Option;
  /**
   * Controls whether react-select shows the "Create X" prompt for the typed input.
   * Return false to suppress (e.g., when the input matches an existing option label).
   * Defaults to react-select's built-in behavior, which compares against option values.
   */
  isValidNewOption?: (inputValue: string) => boolean;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  className?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Light (default) or dark surface for the portaled options menu. */
  surfaceVariant?: PortaledSurfaceVariant;
  /**
   * When true, omit the control's own focus ring so a parent `focus-within` style
   * (e.g. widget settings fields) can own focus chrome.
   */
  suppressControlFocusRing?: boolean;
  /** `id` on the search input — used for initial focus / `htmlFor`. */
  inputId?: string;
  name?: string;
  /**
   * Render the menu on `document.body` with fixed coordinates taken from the
   * control. A scrollport ancestor cannot clip it, and the menu follows the
   * control when that ancestor scrolls.
   */
  menuInPortal?: boolean;
  /**
   * Base test id. The control gets it as is; the search input, each option, each selected chip
   * and each chip's remove button get it with an `-input`, `-option`, `-chip` and `-chip-remove`
   * suffix.
   */
  'data-testid'?: string;
}

/**
 * A multi-select component with the ability to create new options on the fly.
 *
 * Built on top of react-select/creatable to provide functionality not available
 * in the standard shadcn select component.
 *
 * @example
 * ```tsx
 * const [selectedTags, setSelectedTags] = useState<Option[]>([]);
 *
 * <CreatableMultiSelect
 *   options={[
 *     { label: 'React', value: 'react' },
 *     { label: 'TypeScript', value: 'typescript' }
 *   ]}
 *   value={selectedTags}
 *   onChange={setSelectedTags}
 *   onCreateOption={async (input) => ({
 *     label: input,
 *     value: input.toLowerCase()
 *   })}
 *   placeholder="Select or create tags..."
 *   size="md"
 * />
 * ```
 */

function CreatableControl(props: ControlProps<Option, true, GroupBase<Option>>) {
  const innerProps = { ...props.innerProps, 'data-slot': 'creatable-multi-select-control' };
  return <components.Control {...props} innerProps={innerProps} />;
}

/** Select parts that carry test ids derived from `testId`. */
function testIdComponents(testId: string): SelectComponentsConfig<Option, true, GroupBase<Option>> {
  function TestIdControl(props: ControlProps<Option, true, GroupBase<Option>>) {
    const innerProps = {
      ...props.innerProps,
      'data-slot': 'creatable-multi-select-control',
      'data-testid': testId,
    };
    return <components.Control {...props} innerProps={innerProps} />;
  }
  function TestIdInput(props: InputProps<Option, true, GroupBase<Option>>) {
    return <components.Input {...props} data-testid={`${testId}-input`} />;
  }
  function TestIdOption(props: OptionProps<Option, true, GroupBase<Option>>) {
    const innerProps = { ...props.innerProps, 'data-testid': `${testId}-option` };
    return <components.Option {...props} innerProps={innerProps} />;
  }
  function TestIdChip(props: MultiValueGenericProps<Option, true, GroupBase<Option>>) {
    const innerProps = { ...props.innerProps, 'data-testid': `${testId}-chip` };
    return <components.MultiValueContainer {...props} innerProps={innerProps} />;
  }
  function TestIdChipRemove(props: MultiValueRemoveProps<Option, true, GroupBase<Option>>) {
    const innerProps = { ...props.innerProps, 'data-testid': `${testId}-chip-remove` };
    return <components.MultiValueRemove {...props} innerProps={innerProps} />;
  }
  return {
    Control: TestIdControl,
    Input: TestIdInput,
    Option: TestIdOption,
    MultiValueContainer: TestIdChip,
    MultiValueRemove: TestIdChipRemove,
  };
}

/**
 * A dialog's scroll lock cancels the native wheel on a body-portaled menu.
 * Apply the delta to the menu list so the options can still scroll.
 */
function scrollPortaledMenu(event: WheelEvent<HTMLDivElement>) {
  const root = event.currentTarget;
  let node: HTMLElement | null = event.target instanceof HTMLElement ? event.target : root;

  while (node && root.contains(node)) {
    const { overflowY } = getComputedStyle(node);
    const canScroll =
      (overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight;
    if (canScroll) {
      node.scrollTop += event.deltaY;
      event.stopPropagation();
      return;
    }
    node = node.parentElement;
  }
}

function PortaledCreatableMenu(props: MenuProps<Option, true, GroupBase<Option>>) {
  const innerProps = {
    ...props.innerProps,
    'data-slot': 'creatable-multi-select-menu',
    onWheel: (event: WheelEvent<HTMLDivElement>) => {
      props.innerProps.onWheel?.(event);
      scrollPortaledMenu(event);
    },
  };
  return <components.Menu {...props} innerProps={innerProps} />;
}

export const CreatableMultiSelect: React.FC<MultiSelectProps> = ({
  options = [],
  value = [],
  onChange,
  onCreateOption,
  isValidNewOption,
  placeholder = 'Select or create options...',
  isDisabled = false,
  isClearable = true,
  isSearchable = true,
  className = '',
  error,
  helperText,
  size = 'md',
  surfaceVariant = 'light',
  suppressControlFocusRing = false,
  inputId,
  name,
  menuInPortal = false,
  'data-testid': dataTestId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const isDarkSurface = surfaceVariant === 'dark';
  const menuPortalTarget = menuInPortal ? document.body : undefined;

  const selectComponents = useMemo(
    () => ({
      Control: CreatableControl,
      ...(dataTestId ? testIdComponents(dataTestId) : {}),
      ...(menuInPortal ? { Menu: PortaledCreatableMenu } : {}),
    }),
    [menuInPortal, dataTestId],
  );

  const handleCreate = async (inputValue: string) => {
    if (!onCreateOption) {
      // Fallback behavior if no callback provided
      const newOption: Option = {
        label: inputValue,
        value: inputValue.toLowerCase().replace(/\W/g, ''),
      };
      return newOption;
    }

    setIsLoading(true);

    try {
      const newOption = await onCreateOption(inputValue);
      return newOption;
    } catch (error) {
      console.error('Error creating option:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn('w-full', className)}>
      <CreatableSelect
        inputId={inputId}
        name={name}
        isMulti
        isClearable={isClearable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isSearchable={isSearchable}
        isValidNewOption={isValidNewOption}
        onChange={(newValue) => onChange?.(newValue as Option[])}
        onCreateOption={handleCreate}
        options={options}
        placeholder={placeholder}
        value={value}
        unstyled
        components={selectComponents}
        {...(menuPortalTarget
          ? {
              menuPortalTarget,
              menuPosition: 'fixed' as const,
              menuShouldScrollIntoView: false,
              styles: {
                menuPortal: (base: Record<string, unknown>) => ({
                  ...base,
                  zIndex: 1000000000,
                }),
              },
            }
          : {})}
        classNames={{
          control: ({ isFocused }) =>
            cn(
              'flex min-w-0 rounded border px-px py-px cursor-pointer transition-all group',
              'bg-background-surface dark:bg-background-surface dark:hover:bg-background-surface',
              size === 'sm' ? 'min-h-8' : size === 'lg' ? 'min-h-12' : 'min-h-9',
              error
                ? 'border-destructive'
                : isFocused && !suppressControlFocusRing
                  ? 'border-primary ring-[3px] ring-primary/40'
                  : 'border-input',
              !error && !suppressControlFocusRing && 'hover:border-primary/60',
              isDisabled && 'bg-muted opacity-50 cursor-not-allowed',
            ),
          valueContainer: () =>
            cn(
              'flex flex-wrap gap-1 bg-transparent cursor-pointer transition-colors',
              size === 'sm' ? 'px-2 py-0.5' : size === 'lg' ? 'px-3 py-2' : 'px-2 py-1',
            ),
          input: () =>
            'm-0 bg-transparent p-0 text-sm text-foreground caret-foreground appearance-none dark:!bg-transparent',
          placeholder: () => 'text-sm text-interaction-neutral-content-default',
          multiValue: () => 'flex items-center bg-turquoise-500 rounded m-0.5',
          multiValueLabel: () =>
            'text-xs px-1.5 py-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-interaction-primary-content-default',
          multiValueRemove: () =>
            cn(
              'px-1 py-0.5 rounded-r hover:bg-primary/80 transition-colors cursor-pointer',
              '[&_svg]:fill-interaction-primary-content-default',
            ),
          menu: () =>
            cn(
              'border border-border rounded shadow-lg z-[1000000000] text-sm mt-2 bg-background-surface text-foreground-primary',
              isDarkSurface && 'dark [color-scheme:dark]',
            ),
          menuPortal: () => 'tw pointer-events-auto',
          menuList: () => 'p-1 max-h-[180px] overflow-y-auto',
          group: () =>
            'border-t border-border-muted first:border-t-0 mt-2 first:mt-0 pt-1 first:pt-0',
          groupHeading: () =>
            'p-2 text-xs font-medium text-foreground-secondary uppercase tracking-wide',
          option: ({ isFocused, isSelected }) =>
            cn(
              'p-2 text-sm text-foreground-primary cursor-pointer rounded transition-colors',
              (isFocused || isSelected) &&
                (isDarkSurface
                  ? 'bg-background-muted text-foreground-primary'
                  : 'bg-accent text-accent-foreground'),
            ),
          noOptionsMessage: () => 'text-interaction-neutral-content-default text-sm p-3',
          loadingMessage: () => 'text-interaction-neutral-content-default text-sm p-3',
          clearIndicator: () =>
            cn(
              'bg-transparent text-interaction-neutral-content-default p-1.5 cursor-pointer transition-colors',
              'hover:text-foreground',
              '[&_svg]:fill-current',
            ),
          indicatorSeparator: () => 'bg-border w-px my-2',
          dropdownIndicator: () =>
            cn(
              'bg-transparent text-interaction-neutral-content-default p-1.5 cursor-pointer rounded-r transition-colors',
              'hover:text-foreground',
              '[&_svg]:fill-current',
            ),
          singleValue: () => 'text-text-primary text-sm',
        }}
      />
      {(error || helperText) && (
        <div className="mt-1.5">
          {error && (
            <p className={cn('text-sm font-medium text-destructive')} role="alert">
              {error}
            </p>
          )}
          {helperText && !error && (
            <p className={cn('text-sm text-muted-foreground')}>{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CreatableMultiSelect;
